import CircularProgress from '@/Components/CircularProgress/CircularProgress';
import { Procedure } from '@/Components/Procedure/Procedure';
import { useFilingCabinetContext } from '@/Contexts/Authenticated/FilingCabinetContext';
import { NoResults } from '@/Pages/GenericWorklist/Components/NoResults';
import TableStyles from '@style/GenericTable.module.scss';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { t } from 'i18next';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import iconRemarks from '../../../public/icons/iconRemarks.png';
import { type OrderSearchResult, type PatientSearchResult } from '@/Models/GeneralSearch';
import Styles from './GeneralSearch.module.scss';
import { DobTableWrapper } from '@dedalusdiit/dob-components';
import FilingCabinetStore from '@/Stores/FilingCabinetStore';
import GeneralSearchStore from '@/Stores/GeneralSearchStore';
import { FilingCabinetItem } from '@/Models/FilingCabinet/FilingCabinet';

const patientColumnHelper = createColumnHelper<PatientSearchResult>();
const patientColumns = [
    patientColumnHelper.accessor('ID', { id: 'ID', header: 'ID', cell: (info) => info.getValue() }),
    patientColumnHelper.accessor('FullName', { id: 'FullName', header: 'Name', cell: (info) => info.getValue() }),
    patientColumnHelper.accessor('Address', { id: 'Address', header: 'Address', cell: (info) => info.getValue() }),
    patientColumnHelper.accessor('DOBdmy', { id: 'DOBdmy', header: 'dateOfBirthShort', cell: (info) => info.getValue() }),
    patientColumnHelper.accessor('Age', { id: 'Age', header: 'Age', cell: (info) => info.getValue() }),
    patientColumnHelper.accessor('NationalNumber', { id: 'NationalNumber', header: 'NationalRegisterNumber', cell: (info) => info.getValue() }),
    patientColumnHelper.accessor('Remarks', { id: 'Remarks', header: 'PublicRemarks', cell: (info) => info.getValue() }),
    patientColumnHelper.accessor('RemarksPrivate', { id: 'RemarksPrivate', header: 'PrivateRemarks', cell: (info) => {
        if (info.getValue()) {
            return <PrivateRemarks />;
        }
        return '';
    },
    }),
    patientColumnHelper.accessor('InternalNumber', { id: 'InternalNumber', header: 'InternalNumber', cell: (info) => info.getValue() }),
    patientColumnHelper.accessor('ReferenceNumber', { id: 'ReferenceNumber', header: 'ReferenceNumber', cell: (info) => info.getValue() }),
];

const orderColumnHelper = createColumnHelper<OrderSearchResult>();
const orderColumns = [
    orderColumnHelper.accessor('OrderNumber', { id: 'OrderNumber', header: 'OrderNumber', cell: (info) => info.getValue() }),
    orderColumnHelper.accessor('DateTimeImplementation', { id: 'DateTimeImplementation', header: 'Implementation', cell: (info) => info.getValue() }),
    orderColumnHelper.accessor('PatientName', { id: 'PatientName', header: 'PatientName', cell: (info) => info.getValue() }),
    orderColumnHelper.accessor('DateOfBirth', { id: 'DateOfBirth', header: 'dateOfBirthShort', cell: (info) => info.getValue() }),
    orderColumnHelper.accessor('Age', { id: 'Age', header: 'Age', cell: (info) => info.getValue() }),
    orderColumnHelper.accessor('RequestingPhysician', { id: 'RequestingPhysician', header: 'Applicant', cell: (info) => info.getValue() }),
    orderColumnHelper.accessor('Procedures', { id: 'Procedures', header: 'Procedures', cell: (info) => {
        return info.getValue().map((x) => <Procedure key={x.OrderHasProcedure_UID} item={x} />);
    } }),
    orderColumnHelper.accessor('InvoiceAmount', { id: 'InvoiceAmount', header: 'InvoiceAmount', cell: (info) => info.getValue() }),
    orderColumnHelper.accessor('InvoiceStatus', { id: 'InvoiceStatus', header: 'InvoiceStatus', cell: (info) => info.getValue() }),
    orderColumnHelper.accessor('FullStatus', { id: 'FullStatus', header: 'Status', cell: (info) => info.getValue() }),
    orderColumnHelper.accessor('VisitNumber', { id: 'VisitNumber', header: 'Visit', cell: (info) => info.getValue() }),
    orderColumnHelper.accessor('SiteName', { id: 'SiteName', header: 'Site', cell: (info) => info.getValue() }),
];

function PrivateRemarks(): React.ReactElement {
    const { t } = useTranslation();

    return (
        <img height="20px" src={iconRemarks} title={t('PatientHasPrivateRemarks')} alt={t('PatientHasPrivateRemarks')} />
    );
}

interface TablePatientsSearchResultProps {
    isLoaded: boolean;
    patients: PatientSearchResult[];
}
function TablePatientsSearchResult(props: Readonly<TablePatientsSearchResultProps>): React.ReactElement {
    const { isLoaded, patients } = props;
    const navigate = useNavigate();

    const patientsTable = useReactTable({
        columns: patientColumns,
        data: patients,
        getCoreRowModel: getCoreRowModel(),
    });

    const noPatientResults = useMemo(() => patients.length === 0, [patients]);

    return (
        <div className={TableStyles.tabledataOverview}>
            <DobTableWrapper color="primary">
                <table>
                    <thead>
                        {patientsTable.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id}>
                                        <div>{flexRender(t(header.column.columnDef.header as string), header.getContext())}</div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {!isLoaded && (
                            <tr>
                                <td colSpan={50}>
                                    <CircularProgress />
                                </td>
                            </tr>
                        )}
                        {isLoaded && noPatientResults && <NoResults />}
                        {patientsTable.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                onClick={() => navigate('/Patients/Detail/' + row.original.ID)}
                                className={TableStyles.resultRow}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </DobTableWrapper>
        </div>
    );
}

interface TableOrdersSearchResultProps {
    isLoaded: boolean;
    orders: OrderSearchResult[];
}

function TableOrdersSearchResult(props: Readonly<TableOrdersSearchResultProps>): React.ReactElement {
    const { isLoaded, orders } = props;
    const navigate = useNavigate();

    const ordersTable = useReactTable({
        columns: orderColumns,
        data: orders,
        getCoreRowModel: getCoreRowModel(),
    });

    const noOrderResults = useMemo(() => orders.length === 0, [orders]);

    return (
        <div className={TableStyles.tabledataOverview}>
            <DobTableWrapper color="primary">
                <table>
                    <thead>
                        {ordersTable.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id}>
                                        <div>{flexRender(t(header.column.columnDef.header as string), header.getContext())}</div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {!isLoaded && (
                            <tr>
                                <td colSpan={50}>
                                    <CircularProgress />
                                </td>
                            </tr>
                        )}
                        {isLoaded && noOrderResults && <NoResults />}
                        {ordersTable.getRowModel().rows.map((row) => (
                            <tr
                                key={row.id}
                                onClick={() => navigate('/Orders/Detail/' + row.original.ID)}
                                className={TableStyles.resultRow}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </DobTableWrapper>
        </div>
    );
}

function GeneralSearch({ initialState }: Readonly<{ initialState: FilingCabinetItem }>): React.ReactElement {
    const { t } = useTranslation();
    const params = useParams();

    const fcContext = useFilingCabinetContext();
    const store = useMemo(() => new GeneralSearchStore(new FilingCabinetStore(initialState, fcContext), t, params.term ?? ''), [initialState]);

    return (
        <div className={Styles.generalSearch + ' scrollableContent'}>
            <div className={Styles.pageHeader}>{t('GeneralSearch')} - {params.term}</div>
            <div>
                <details open={store.state.patientsOpen} onToggle={(e: React.ToggleEvent<HTMLDetailsElement>) => {
                    store.setPatientsOpen(e.currentTarget.open); 
                }}>
                    <summary className={Styles.header}>
                        <span>{t('Patients')} ({store.state.patients.length})</span>
                    </summary>
                    <TablePatientsSearchResult patients={store.state.patients} isLoaded={store.state.isLoaded} />
                </details>
                <br />
                <br />

                <details open={store.state.ordersOpen} onToggle={(e: React.ToggleEvent<HTMLDetailsElement>) => {
                    store.setOrdersOpen(e.currentTarget.open); 
                }}>
                    <summary className={Styles.header}>
                        <span>{t('Orders')} ({store.state.orders.length})</span>
                    </summary>
                    <TableOrdersSearchResult orders={store.state.orders} isLoaded={store.state.isLoaded} />
                </details>
            </div>
        </div>
    );
}

export default GeneralSearch;
